import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getSiteMaintenance, SiteMaintenanceItem } from '../../api-services/reporting-api/siteMaintenance';
import { BRAND_NAME } from '../../constants/Branding';
import MaintenanceImg from './Maintenance.png';
import './SiteMaintenance.scss';

type Countdown = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
};

function parseCountdown(utcIsoTimestamp: string): Countdown {
  const target = new Date(utcIsoTimestamp).getTime();
  const now = Date.now();
  const diff = Math.max(0, target - now);
  const isPast = target <= now;
  if (isPast) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true };
  }
  const seconds = Math.floor((diff / 1000) % 60);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  return { days, hours, minutes, seconds, isPast: false };
}

function formatUtcLabel(utcIsoTimestamp: string): string {
  try {
    const d = new Date(utcIsoTimestamp);
    return (
      d.toLocaleString('en-GB', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }) + ' UTC'
    );
  } catch {
    return utcIsoTimestamp;
  }
}

const SiteMaintenance: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [maintenanceInfo, setMaintenanceInfo] = useState<SiteMaintenanceItem | null>(null);
  const [countdown, setCountdown] = useState<Countdown | null>(null);

  const tick = useCallback((ts: string) => {
    setCountdown(parseCountdown(ts));
  }, []);

  // Maintenance fetch commented out – no API call when visiting /site-maintenance
  // useEffect(() => {
  //   let cancelled = false;
  //   getSiteMaintenance()
  //     .then((list) => {
  //       if (cancelled) return;
  //       // empty array → no maintenance record, treat as site up
  //       setMaintenanceInfo(list.length > 0 ? list[0] : null);
  //     })
  //     .catch(() => {
  //       if (!cancelled) setMaintenanceInfo(null);
  //     })
  //     .finally(() => {
  //       if (!cancelled) setLoading(false);
  //     });
  //   return () => {
  //     cancelled = true;
  //   };
  // }, []);

  useEffect(() => {
    if (!maintenanceInfo?.isSiteUnderMaintenance || !maintenanceInfo?.downtimeTimestamp) {
      if (!loading) history.replace('/');
      return;
    }
    tick(maintenanceInfo.downtimeTimestamp);
  }, [loading, maintenanceInfo, history, tick]);

  useEffect(() => {
    if (!maintenanceInfo?.isSiteUnderMaintenance || !maintenanceInfo?.downtimeTimestamp) return;
    const id = setInterval(() => tick(maintenanceInfo.downtimeTimestamp), 1000);
    return () => clearInterval(id);
  }, [maintenanceInfo, tick]);

  if (loading) {
    return (
      <div className="site-maintenance site-maintenance--loading">
        <img src={MaintenanceImg} className="site-maintenance__logo" alt={BRAND_NAME} />
        <div className="site-maintenance__spinner" />
      </div>
    );
  }

  const downtimeTimestamp = maintenanceInfo?.downtimeTimestamp;
  if (!downtimeTimestamp || !countdown) {
    return null;
  }

  return (
    <div className="site-maintenance">
      <img src={MaintenanceImg} className="site-maintenance__logo" alt={BRAND_NAME} />
      <h1 className="site-maintenance__title">Site under maintenance</h1>
      <p className="site-maintenance__subtitle">
        We&apos;ll be back at {formatUtcLabel(downtimeTimestamp)}
      </p>
      {countdown.isPast ? (
        <p className="site-maintenance__countdown site-maintenance__countdown--ended">
          Maintenance in progress. Please try again shortly.
        </p>
      ) : (
        <div className="site-maintenance__countdown">
          <div className="site-maintenance__countdown-block">
            <span className="site-maintenance__countdown-value">{String(countdown.hours).padStart(2, '0')}</span>
            <span className="site-maintenance__countdown-label">Hours</span>
          </div>
          <div className="site-maintenance__countdown-sep">:</div>
          <div className="site-maintenance__countdown-block">
            <span className="site-maintenance__countdown-value">{String(countdown.minutes).padStart(2, '0')}</span>
            <span className="site-maintenance__countdown-label">Minutes</span>
          </div>
          <div className="site-maintenance__countdown-sep">:</div>
          <div className="site-maintenance__countdown-block">
            <span className="site-maintenance__countdown-value">{String(countdown.seconds).padStart(2, '0')}</span>
            <span className="site-maintenance__countdown-label">Seconds</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteMaintenance;
